import { createContext, useContext, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { isEqualCurrency } from '../utils/isEqualCurrency';
import { useEventContext } from './EventContext';
import { useRegistrationFlowContext } from './RegistrationFlowContext';

export const DonationsContext = createContext(null);

export const useDonationsContext = () => useContext(DonationsContext);

export const withDonationsContext = (Component) => (props) => {
  const contextValue = useDonationsContext();

  return <Component {...props} {...contextValue} />;
};

export const DonationsProvider = ({ children }) => {
  const { event } = useEventContext();

  const { donations } = event;

  const { selectedCurrency, setValuesStep2, valuesStep2 } =
    useRegistrationFlowContext();

  const { pathname } = useLocation();

  const [isOpen, setOpen] = useState(false);
  const [displayed, setDisplayed] = useState(false);
  const [onSuccess, setOnSuccess] = useState(null);

  const openModal = ({ onSubmitSuccess }) => {
    if (!displayed) {
      setDisplayed(true);
    }

    setOnSuccess(() => onSubmitSuccess);
    setOpen(true);
  };

  const closeModal = () => setOpen(false);

  const onSubmit = ({ donations }) => {
    setValuesStep2({
      network_events_booking: {
        ...valuesStep2.network_events_booking,
        donations_attributes: donations,
      },
    });
  };

  const isEditable = pathname.includes('registration');

  const providerState = useMemo(
    () => ({
      donations: (donations || []).filter(({ currency }) =>
        isEqualCurrency(currency, selectedCurrency),
      ),
      displayed,
      isDonationModalOpen: isOpen,
      isEditable,
      openModal,
      closeModal,
      onSubmit,
      onSubmitSuccess: async ({ donated }) => {
        await onSuccess?.({ donated });

        closeModal();
      },
    }),
    [donations, selectedCurrency, isEditable, displayed, isOpen],
  );

  return (
    <DonationsContext.Provider value={providerState}>
      {children}
    </DonationsContext.Provider>
  );
};
